// What a checkout's answer says about the sale it carried (plan section 8,
// Draft safety).
//
// The pending record says "a sale may exist". It may go only when the till's
// order store, which takes the sale with the core's own rules, has said what
// became of it: a 2xx with the order in it, or a 4xx refusal in the API's own
// words, which made no order. Every other answer
// leaves the sale's fate unknown. The record then stays, and Retry sends the
// same key, so the server answers with the order if it made one.
//
// A refusal says only that the send it answers made no order. Some come
// before the server reads the key (keyFoundFree), so after a send whose
// answer was lost they say nothing about the sale that send may have placed:
// a resend ends the record only on a refusal that says what the key holds
// (keyAnswered), and asks the key when the refusal names the discount. One
// such refusal comes even on a first send, that of an API older than
// discounts, so the till asks the key before it takes any refusal of the
// discount itself as an answer (refusesTheDiscount, src/till/sell.svelte.ts).
// The answers that leave the fate unknown are:
//
//   5xx      a server that failed, perhaps after it committed, or the gap
//            while a deploy swaps the server;
//   423      a locked session, so the sale goes once a cashier unlocks;
//   no body  an empty answer, or a proxy's page instead of the API's;
//   nothing  a network error, which the caller sees as a throw;
//   resend   a resend's 4xx that says nothing about the key (keyAnswered),
//            such as a session's 401 or the schema's 422.
//
// The till's order store answers every sale (#371). It has one 5xx of its own
// that is an answer to a first send: 503 with CLOCK_NOT_TIED or MENU_NOT_READ
// (storeNotReady), which it gives before it reads anything, while it has had
// no server answer since it started or has not read its till's menu yet. No
// earlier send of a first send's key exists, so that sale was not made; a
// resend's says nothing about the send whose answer was lost, and keeps the
// record like any 5xx.

export type Fate = 'placed' | 'refused' | 'unknown';

/**
 * The codes the till's order store refuses a sale with while it is not ready
 * to sell, before it reads the request (internal/orderstore/openapi.json).
 */
const NOT_READY = ['CLOCK_NOT_TIED', 'MENU_NOT_READ'];

/**
 * Whether the till's order store refused a call because it is not ready to
 * sell yet: no server answer since it started, or no menu read since it
 * paired. A first send it refuses so made no sale; the cashier tries again in
 * a moment.
 */
export function storeNotReady(status: number, problem: Problem | null): boolean {
	return status === 503 && problem !== null && notReadyCode(problem.code);
}

/** Whether a refusal's code is the order store's own not-ready refusal. */
export function notReadyCode(code: string): boolean {
	return NOT_READY.includes(code);
}

/**
 * The body the API sends with every refusal: it always names a code, and the
 * order routes' refusals name the field to change in `path`, such as
 * `discount` or `discount.discountId` (UC-35).
 */
export interface Problem {
	code: string;
	message?: string;
	details?: {
		totalMinor?: number;
		number?: number;
		path?: string;
		order_id?: string;
		/** "required": the store wants a manager's approval for it (UC-16.R9). */
		approval?: string;
	};
}

/**
 * The words the API's request check puts on a body its schema refuses, kept as
 * the message by internal/api/errors.go. features/api/error-shape.feature pins
 * them, with the path, on a field no API knows.
 */
const SCHEMA_REFUSED = 'validation failed';

/**
 * Whether a refusal names the discount as a whole, the field `discount`
 * itself, whatever its code and its words (UC-35, INT-7). An API older than
 * discounts, which a revert put back, refuses that field before it reads the
 * key. So such a refusal of a sale that carries a discount says nothing yet
 * about the sale, which an API before the revert may have placed: the till
 * asks the key before it ends the pending record (askTheKey,
 * src/till/sell.svelte.ts). The current API refuses the discount as a whole
 * only after it read the key, and asking then costs one more request. Only
 * the path decides, never the message, which a library upgrade or an edit to
 * internal/api/errors.go may reword.
 */
export function refusesTheDiscount(problem: Problem | null): boolean {
	return problem?.details?.path === 'discount';
}

/**
 * Whether a refusal says, in the schema's words, that the API does not know
 * `discount` at all (UC-35): an API older than discounts, which a revert put
 * back, refuses the field as one its schema does not allow, naming the field
 * itself. The current API refuses a discount by a rule, in its own words, or
 * names the part of it that is wrong. It only picks what the cashier is told
 * once the key has answered; whether the key is asked never rests on these
 * words (refusesTheDiscount).
 */
export function discountUnknown(problem: Problem | null): boolean {
	return (
		problem?.code === 'VALIDATION_ERROR' &&
		problem.details?.path === 'discount' &&
		problem.message === SCHEMA_REFUSED
	);
}

/**
 * Whether a checkout's refusal proves that its Idempotency-Key placed no sale
 * (INT-7). The API answers a known key before any rule (uc05_place_order.go),
 * so a refusal by one of its rules, a 422 in that rule's own code such as
 * TOTAL_MISMATCH, comes only after the key was found free. The answers that
 * can come before the key is read prove nothing: the schema's
 * VALIDATION_ERROR, a session's 401, 403 or 423, a missing key's 400, and a
 * 404 for a path the API does not serve.
 */
export function keyFoundFree(status: number, problem: Problem | null): boolean {
	return status === 422 && problem !== null && problem.code !== 'VALIDATION_ERROR';
}

/**
 * Whether a checkout's refusal says what its Idempotency-Key holds (INT-7): a
 * sale, when the key placed one under another body (409
 * IDEMPOTENCY_MISMATCH), or none, when the API refused by a rule it runs only
 * after it found the key free (keyFoundFree), a removed driver (409
 * DRIVER_INACTIVE), which the till's order store judges only after it read
 * the key (UC-4.R6), or did not know a thing the
 * sale names: an item, a delivery area or a list discount (UC-35). Only the
 * use case answers that, with a 404 that names the field, such as `lines[0]`
 * or `discount.discountId`; the 404 for a path the API does not serve names
 * none. Any other refusal can come before the key is read, so it says nothing
 * about a send of this key whose answer was lost.
 */
export function keyAnswered(status: number, problem: Problem | null): boolean {
	if (problem === null) return false;
	if (status === 409)
		return problem.code === 'IDEMPOTENCY_MISMATCH' || problem.code === 'DRIVER_INACTIVE';
	if (status === 404) return Boolean(problem.details?.path);
	return keyFoundFree(status, problem);
}

/**
 * Whether a refusal names the menu version the sale names (#343). The current
 * API refuses a version that ran out by a rule, `TOTAL_MISMATCH`, which names
 * no field, and refuses the field itself only when it is not a uuid, which
 * the till never sends: it sends the version its menu named. So a refusal of
 * the field comes from an API older than menu versions, which a revert put
 * back, before it reads the key: it says nothing about the sale. Only the path
 * decides, never the words.
 */
export function refusesTheMenuVersion(problem: Problem | null): boolean {
	return problem?.details?.path === 'menuVersion';
}

/**
 * Whether a refusal names a dine-in's table in the schema's code (UC-39,
 * INT-7). An API older than tables, which a revert put back, refuses
 * `tableId` as a field its schema does not allow, before it reads the key. The
 * current API refuses the table by its own rules, TABLE_REQUIRED,
 * TABLE_UNAVAILABLE or a 404, only after it read the key, and its schema
 * refuses `tableId` only for a value no till sends. So such a refusal of a sale
 * that names a table says nothing yet about the sale: the till asks the key
 * without the table before it ends the pending record (askTheKey,
 * src/till/sell.svelte.ts). Only the path and the code decide.
 */
export function refusesTheTable(problem: Problem | null): boolean {
	return problem?.code === 'VALIDATION_ERROR' && problem.details?.path === 'tableId';
}

/** Whether a refusal names the discount or a part of it (UC-35). */
export function onTheDiscount(problem: Problem | null): boolean {
	const path = problem?.details?.path ?? '';
	return path === 'discount' || path.startsWith('discount.');
}

/** The API's problem body, or null when the body is not one. */
export function problemOf(body: unknown): Problem | null {
	if (typeof body !== 'object' || body === null) return null;
	const code = (body as { code?: unknown }).code;
	return typeof code === 'string' && code !== '' ? (body as Problem) : null;
}

/** What became of the sale, as far as this answer says. */
export function checkoutFate(status: number, data: unknown, error: unknown): Fate {
	if (status >= 200 && status < 300) return data ? 'placed' : 'unknown';
	if (status === 423 || status >= 500) return 'unknown';
	if (status >= 400 && problemOf(error) !== null) return 'refused';
	return 'unknown';
}
